import type { RouteObject } from 'react-router';
import { RootLayout } from './layout/RootLayout';

// Lazy data-router pattern: each `lazy` returns { Component } so React Router
// awaits the import before rendering. Compatible with createBrowserRouter().
//
// Chunk-retry guard: a failed dynamic import almost always means a new deploy
// replaced the hashed chunk this (stale) tab is asking for — the classic
// "Failed to fetch dynamically imported module" error. We reload ONCE to pull
// the fresh index.html + chunks. A sessionStorage flag prevents a reload loop
// when the failure is genuine (offline, real 404); it's cleared on any
// successful load so a later deploy can retry again.
const CHUNK_RELOAD_KEY = 'chunk-reload-attempt';

const loadRoute =
  (loader: () => Promise<{ default: React.ComponentType }>) =>
  async (): Promise<{ Component: React.ComponentType }> => {
    try {
      const mod = await loader();
      try {
        sessionStorage.removeItem(CHUNK_RELOAD_KEY);
      } catch {
        /* sessionStorage unavailable — ignore */
      }
      return { Component: mod.default };
    } catch (err) {
      let alreadyReloaded = true;
      try {
        alreadyReloaded = !!sessionStorage.getItem(CHUNK_RELOAD_KEY);
      } catch {
        /* ignore */
      }
      if (typeof window !== 'undefined' && !alreadyReloaded) {
        try {
          sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
        } catch {
          /* ignore */
        }
        window.location.reload();
        // Never resolve — the reload is taking over, so React Router keeps the
        // HydrateFallback up instead of flashing an error boundary.
        return new Promise<{ Component: React.ComponentType }>(() => {});
      }
      throw err;
    }
  };

// Initial-hydration fallback shown by React Router v7 while lazy chunks load
// before client takeover. Inlined as an arrow function (rather than a named
// export) so this file stays Fast-Refresh-friendly. Same neutral background
// as RootLayout so the transition is visually invisible. Without this, R7
// logs: "No `HydrateFallback` element provided to render during initial
// hydration".
export const routes: RouteObject[] = [
  {
    path: '/',
    Component: RootLayout,
    HydrateFallback: () => <div className="min-h-screen bg-neutral-950" aria-hidden="true" />,
    children: [
      { index: true, lazy: loadRoute(() => import('./pages/HomePage')) },
      { path: 'hosting', lazy: loadRoute(() => import('./pages/HostingPage')) },
      // Partner acquisition link (L3). `/p/<token>` captures the token for the
      // session and forwards to /hosting — it is NOT a second packages page.
      // Cloning /hosting would create two surfaces that disagree about prices
      // the first time one is updated. Nothing partner-branded is rendered
      // (CT-10: SightHost does not white-label).
      { path: 'p/:token', lazy: loadRoute(() => import('./pages/PartnerLandingPage')) },
      { path: 'domains', lazy: loadRoute(() => import('./pages/DomainsPage')) },
      // Fibre disabled 2026-05-08 — service is not yet active. Page
      // file FibrePage.tsx is preserved; uncomment this line and the
      // matching nav entries (navigation.ts mainNav + footerNav.services
      // + Footer.tsx columns) to re-enable when the service launches.
      // { path: 'fibre', lazy: loadRoute(() => import('./pages/FibrePage')) },
      { path: 'about', lazy: loadRoute(() => import('./pages/AboutPage')) },
      { path: 'why-sighthost', lazy: loadRoute(() => import('./pages/WhySightHostPage')) },
      { path: 'support', lazy: loadRoute(() => import('./pages/SupportPage')) },
      // Care + Email Setup live on the single /services/care surface
      // since 2026-05-09. /services/email-setup is 301'd to
      // /services/care#email-setup via marketing/public/_redirects.
      { path: 'services/care', lazy: loadRoute(() => import('./pages/services/CarePlanPage')) },
      { path: 'services/website-design', lazy: loadRoute(() => import('./pages/services/WebsiteDesignPage')) },
      { path: 'contact', lazy: loadRoute(() => import('./pages/ContactPage')) },
      { path: 'terms', lazy: loadRoute(() => import('./pages/TermsPage')) },
      { path: 'privacy', lazy: loadRoute(() => import('./pages/PrivacyPage')) },
      { path: 'aup', lazy: loadRoute(() => import('./pages/AupPage')) },
      { path: 'sla', lazy: loadRoute(() => import('./pages/SlaPage')) },
      { path: 'refund', lazy: loadRoute(() => import('./pages/RefundPage')) },
      { path: 'cookies', lazy: loadRoute(() => import('./pages/CookiesPage')) },
      { path: '*', lazy: loadRoute(() => import('./pages/NotFoundPage')) },
    ],
  },
];
